/**
 * Online-meeting links in invites and email: which platform, the https join link, a native-app link
 * where the vendor documents one, and the meeting ID / passcode printed next to it.
 *
 * Native app links — only where the vendor documents the scheme:
 *   - Teams: the https meetup-join link with `msteams://` (Microsoft Teams deep-link docs).
 *   - Zoom:  `zoommtg://zoom.us/join?action=join&confno=<id>&pwd=<pwd>` (Zoom client URL scheme).
 * Webex (incl. Cisco Jabber meetings), Google Meet, GoTo, Zoho and the rest publish no scheme; their
 * https join page offers to open the installed app itself, so Join is the https link.
 */
export interface MeetingLink {
    /** "Microsoft Teams", "Zoom", … */
    provider: string;
    /** Button text, e.g. "Join Teams meeting". */
    label: string;
    /** The platform's brand colour for the Join button. */
    color: string;
    /** The https join link as it appeared in the message. */
    url: string;
    /** Opens the platform's desktop app directly, where documented. */
    appUrl?: string;
    meetingId?: string;
    passcode?: string;
}

interface Provider {
    name: string;
    label: string;
    color: string;
    /**
     * Hosts the link must be on (exact, or a subdomain of one) — a look-alike domain in a phishing
     * mail never gets a "Join Teams meeting" button.
     */
    hosts: string[];
    /** Path shapes that are a meeting join, not just any page on that host. */
    path: RegExp;
    idFromUrl?: (u: URL) => string | undefined;
    appUrl?: (u: URL, id: string | undefined) => string | undefined;
}

const connectHost = (() => {
    try { return new URL(import.meta.env.VITE_CONNECT_WEB_URL ?? "").hostname; } catch { return ""; }
})();

const PROVIDERS: Provider[] = [
    {
        name: "Microsoft Teams", label: "Join Teams meeting", color: "#5b5fc7",
        hosts: ["teams.microsoft.com", "teams.live.com", "teams.microsoft.us"],
        path: /^\/(?:l\/meetup-join|meet|v2|dl\/launcher)\//i,
        idFromUrl: (u) => /^\/meet\/(\d{6,})/.exec(u.pathname)?.[1],
        // Documented for meetup-join links; the newer /meet/<id> short links have no documented app form.
        appUrl: (u) => (/\/l\/meetup-join\//i.test(u.pathname) && u.hostname.endsWith("teams.microsoft.com")
            ? `msteams://${u.host}${u.pathname}${u.search}`
            : undefined)
    },
    {
        name: "Zoom", label: "Join Zoom meeting", color: "#0b5cff",
        hosts: ["zoom.us", "zoom.com", "zoomgov.com"],
        path: /^\/(?:j|w|s|my|wc\/join)\/[\w.-]+/i,
        idFromUrl: (u) => /^\/(?:j|w|s|wc\/join)\/(\d{9,11})\b/.exec(u.pathname)?.[1],
        appUrl: (u, id) => {
            if (!id || !/^\d{9,11}$/.test(id)) return undefined;
            const pwd = u.searchParams.get("pwd");
            return `zoommtg://zoom.us/join?action=join&confno=${id}${pwd ? `&pwd=${encodeURIComponent(pwd)}` : ""}`;
        }
    },
    {
        name: "Google Meet", label: "Join Google Meet", color: "#188038",
        hosts: ["meet.google.com"],
        path: /^\/(?:[a-z]{3}-[a-z]{4}-[a-z]{3}\b|lookup\/)/i,
        idFromUrl: (u) => /^\/([a-z]{3}-[a-z]{4}-[a-z]{3})\b/i.exec(u.pathname)?.[1]
    },
    {
        name: "Webex", label: "Join Webex meeting", color: "#07c160",
        hosts: ["webex.com"],
        path: /^\/(?:meet\/|join\/|[\w-]+\/j\.php|wbxmjs\/|webappng\/sites\/[\w-]+\/meeting\/)/i
    },
    {
        name: "Zoho Meeting", label: "Join Zoho meeting", color: "#e42527",
        hosts: ["meeting.zoho.com", "meeting.zoho.in", "meeting.zoho.eu", "meeting.zoho.com.au", "meeting.zoho.jp",
            "meeting.zoho.sa", "meeting.zohocloud.ca"],
        path: /join/i,
        idFromUrl: (u) => u.searchParams.get("key") ?? undefined
    },
    {
        name: "GoTo Meeting", label: "Join GoTo meeting", color: "#f68d2e",
        hosts: ["meet.goto.com", "app.goto.com", "global.gotomeeting.com", "gotomeeting.com"],
        path: /^\/(?:(?:join|meeting)\/)?\d{9,10}\b/i,
        idFromUrl: (u) => /(\d{9,10})/.exec(u.pathname)?.[1]
    },
    {
        name: "GoTo Meeting", label: "Join GoTo meeting", color: "#f68d2e",
        hosts: ["gotomeet.me"],
        path: /^\/[\w.-]{3,}\/?$/
    },
    {
        name: "Jitsi Meet", label: "Join Jitsi meeting", color: "#1d76ba",
        hosts: ["meet.jit.si", "8x8.vc"],
        path: /^\/[\w.-]{3,}/
    },
    {
        name: "Amazon Chime", label: "Join Chime meeting", color: "#232f3e",
        hosts: ["chime.aws"],
        path: /^\/\d{10}\b/,
        idFromUrl: (u) => /^\/(\d{10})/.exec(u.pathname)?.[1]
    },
    {
        name: "RingCentral", label: "Join RingCentral meeting", color: "#ff7a00",
        hosts: ["v.ringcentral.com", "meetings.ringcentral.com"],
        path: /^\/(?:join|j)\/\d+|^\/\d{9,}/i,
        idFromUrl: (u) => /(\d{9,})/.exec(u.pathname)?.[1]
    },
    {
        name: "BlueJeans", label: "Join BlueJeans meeting", color: "#1a73e8",
        hosts: ["bluejeans.com"],
        path: /^\/\d{6,}/,
        idFromUrl: (u) => /^\/(\d{6,})/.exec(u.pathname)?.[1]
    },
    {
        name: "Skype", label: "Join Skype meeting", color: "#00aff0",
        hosts: ["join.skype.com"],
        path: /^\/[\w-]{6,}/
    },
    {
        name: "Whereby", label: "Join Whereby room", color: "#6b4fbb",
        hosts: ["whereby.com"],
        path: /^\/[\w-]{3,}\/?$/
    },
    {
        name: "Athavita Connect", label: "Join Connect meeting", color: "#6264A7",
        hosts: ["connect.athavita.com", ...(connectHost ? [connectHost] : [])],
        path: /^\/(?:meeting|guest)\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
    }
];

const URL_RE = /https?:\/\/[^\s<>"'`]+/gi;
// Webex prints "Meeting number (access code): 2641 123 4567" — allow a short bracketed aside.
const ID_RE = /\b(?:meeting\s*id|meeting\s*number|meeting\s*key|webinar\s*id|conference\s*id|access\s*code)(?:\s*\([^)]{0,24}\))?\s*[:#]?\s*([0-9][0-9 \-]{4,22}[0-9])/i;
const PASSCODE_RE = /\b(?:passcode|pass\s*code|password|meeting\s*password|pin)\s*[:#]\s*([^\s<>"']{3,32})/i;

function onHost(hostname: string, hosts: string[]): boolean {
    const h = hostname.toLowerCase();
    return hosts.some((d) => h === d || h.endsWith(`.${d}`));
}

/**
 * The first online-meeting join link in the given text(s), with its meeting ID and passcode when the
 * text prints them. Null when there is no link from a known platform.
 */
export function findMeetingLink(...texts: Array<string | null | undefined>): MeetingLink | null {
    const text = texts.filter(Boolean).join("\n");
    if (!text) return null;
    for (const raw of text.match(URL_RE) ?? []) {
        const candidate = raw.replace(/[.,;:!?)\]}>'"]+$/, "").replace(/&amp;/g, "&");
        let u: URL;
        try { u = new URL(candidate); } catch { continue; }
        if (u.protocol !== "https:" && u.protocol !== "http:") continue;
        const p = PROVIDERS.find((pr) => onHost(u.hostname, pr.hosts) && pr.path.test(u.pathname + u.search));
        if (!p) continue;
        const idInUrl = p.idFromUrl?.(u);
        const idInText = ID_RE.exec(text)?.[1]?.replace(/\s+/g, " ").trim();
        const passcode = PASSCODE_RE.exec(text)?.[1];
        const meetingId = idInText ?? idInUrl;
        return {
            provider: p.name,
            label: p.label,
            color: p.color,
            url: u.toString(),
            appUrl: p.appUrl?.(u, (idInUrl ?? idInText)?.replace(/\D/g, "") || undefined),
            meetingId,
            passcode
        };
    }
    return null;
}

/**
 * Text to search in an email's HTML: every link target first (an invite's visible text is often just
 * "Join the meeting now"), then the readable text with line breaks kept, so "Passcode: X" doesn't run
 * into the next line. DOMParser documents are inert — no scripts run and nothing loads.
 */
export function meetingTextFromHtml(html: string): string {
    if (!html) return "";
    const withBreaks = html.replace(/<(?:br|\/p|\/div|\/tr|\/li|\/h[1-6]|\/table)\b[^>]*>/gi, "$&\n");
    const doc = new DOMParser().parseFromString(withBreaks, "text/html");
    const hrefs = Array.from(doc.querySelectorAll("a[href]"), (a) => a.getAttribute("href") ?? "")
        .filter((h) => /^https?:/i.test(h));
    return `${hrefs.join("\n")}\n${doc.body?.textContent ?? ""}`;
}
